import React, { useState, useEffect } from 'react';
import { t } from '../lib/i18n';
import { researchContractors, createContractor } from '../lib/api';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Search, Plus, Loader2, Globe, TrendingUp, AlertTriangle,
  Building, Star, Shield, MapPin, Lightbulb, UserPlus
} from 'lucide-react';

const CONTRACTOR_TYPES = [
  { value: 'klient', label_pl: 'Klient / Odbiorca', label_en: 'Client / Buyer' },
  { value: 'dostawca', label_pl: 'Dostawca', label_en: 'Supplier' },
  { value: 'projektant', label_pl: 'Projektant', label_en: 'Designer' },
  { value: 'wykonawca', label_pl: 'Wykonawca', label_en: 'Contractor' },
  { value: 'partner', label_pl: 'Partner', label_en: 'Partner' },
];

export default function ContractorResearch({ lang, tenantId }) {
  const [query, setQuery] = useState('');
  const [industry, setIndustry] = useState('');
  const [contractorType, setContractorType] = useState('');
  const [region, setRegion] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [addingId, setAddingId] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [decisionRoles, setDecisionRoles] = useState([]);

  useEffect(() => {
    // Load available industries and roles from tenant config
    api.get('/settings/industries').then(r => {
      setIndustries(r.data || []);
    }).catch(() => {});
    api.get('/contractors/filters', { params: { tenant_id: tenantId } }).then(r => {
      setDecisionRoles(r.data?.decision_roles || []);
    }).catch(() => {});
  }, [tenantId]);

  async function handleSearch() {
    if (!query.trim()) {
      toast.error(lang === 'pl' ? 'Wpisz zapytanie' : 'Enter a query');
      return;
    }
    setLoading(true);
    setResults(null);
    try {
      const data = await researchContractors(query, industry, region, tenantId);
      setResults(data);
    } catch (err) {
      toast.error(lang === 'pl' ? 'Błąd wyszukiwania' : 'Search error');
    }
    setLoading(false);
  }

  async function handleAddContractor(contractor) {
    setAddingId(contractor.company);
    try {
      await createContractor({
        name: contractor.name,
        company: contractor.company,
        notes: `${contractor.specialization || ''}\n${contractor.notes || ''}\nŹródło: AI Research`.trim(),
        tags: [contractor.industry, contractor.region].filter(Boolean),
        contractor_type: contractorType || '',
        industry: contractor.industry || industry || '',
        specialization: contractor.specialization || '',
        city: contractor.region || region || '',
      }, tenantId);
      toast.success(lang === 'pl' ? `Dodano ${contractor.company}` : `Added ${contractor.company}`);
    } catch (err) {
      toast.error(lang === 'pl' ? 'Błąd dodawania' : 'Add error');
    }
    setAddingId(null);
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') handleSearch();
  }

  return (
    <div className="space-y-6" data-testid="contractor-research-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
          {lang === 'pl' ? 'Wyszukiwanie kontrahentów' : 'Contractor Discovery'}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {lang === 'pl' ? 'AI znajdzie potencjalnych dostawców i kontrahentów' : 'AI will find potential suppliers and contractors'}
        </p>
      </div>

      {/* Search Form */}
      <Card className="border border-border">
        <CardContent className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <Label className="text-xs">{lang === 'pl' ? 'Kogo szukasz?' : 'Who are you looking for?'}</Label>
              <Input
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={lang === 'pl' ? 'np. architekt wnętrz specjalizujący się w hotelach' : 'e.g. interior architect specializing in hotels'}
                className="mt-1"
                data-testid="research-query-input"
              />
            </div>
            <div>
              <Label className="text-xs">{lang === 'pl' ? 'Typ kontrahenta' : 'Contractor type'}</Label>
              <Select value={contractorType || '__any__'} onValueChange={v => setContractorType(v === '__any__' ? '' : v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any__">{lang === 'pl' ? 'Dowolny typ' : 'Any type'}</SelectItem>
                  {CONTRACTOR_TYPES.map(ct => (
                    <SelectItem key={ct.value} value={ct.value}>{lang === 'pl' ? ct.label_pl : ct.label_en}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">{lang === 'pl' ? 'Branża' : 'Industry'}</Label>
              <Select value={industry || '__any__'} onValueChange={v => setIndustry(v === '__any__' ? '' : v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any__">{lang === 'pl' ? 'Dowolna branża' : 'Any industry'}</SelectItem>
                  {industries.map(ind => (
                    <SelectItem key={ind.key} value={ind.name}>{ind.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{lang === 'pl' ? 'Region' : 'Region'}</Label>
              <Input
                value={region}
                onChange={e => setRegion(e.target.value)}
                placeholder={lang === 'pl' ? 'np. Warszawa, Małopolska' : 'e.g. Warsaw, Europe'}
                className="mt-1"
              />
            </div>
            <div className="flex items-end">
              <Button onClick={handleSearch} disabled={loading || !query.trim()} className="w-full gap-2" data-testid="research-search-button">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                {lang === 'pl' ? 'Szukaj' : 'Search'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Loading */}
      {loading && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin text-primary" />
            {lang === 'pl' ? 'Adam przeszukuje rynek...' : 'Adam is searching the market...'}
          </div>
          {[1,2,3].map(i => <Skeleton key={i} className="h-32 w-full" />)}
        </div>
      )}

      {/* Results */}
      {results && (
        <div className="space-y-4">
          {/* Market Insights */}
          {results.market_insights && (
            <Card className="border border-primary/20 bg-primary/5">
              <CardContent className="p-4">
                <div className="flex items-start gap-2">
                  <Globe className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-medium text-primary mb-1">
                      {lang === 'pl' ? 'Przegląd rynku' : 'Market Overview'}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{results.market_insights}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recommendation */}
          {results.recommendation && (
            <Card className="border border-[rgba(216,176,122,0.22)] bg-[rgba(216,176,122,0.05)]">
              <CardContent className="p-4">
                <div className="flex items-start gap-2">
                  <Lightbulb className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-medium text-accent mb-1">
                      {lang === 'pl' ? 'Rekomendacja Adama' : "Adam's Recommendation"}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{results.recommendation}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Contractors List */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-muted-foreground">
              {lang === 'pl' ? `Znaleziono ${results.contractors?.length || 0} kontrahentów` : `Found ${results.contractors?.length || 0} contractors`}
            </h3>
            {(results.contractors || []).map((c, i) => (
              <Card key={i} className="border border-border hover:border-border/80 transition-colors duration-150">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Building className="w-4 h-4 text-primary flex-shrink-0" />
                        <h4 className="text-sm font-semibold">{c.company}</h4>
                        <Badge variant="outline" className={`text-xs ${
                          c.relevance_score >= 0.8 ? 'border-emerald-400/30 text-emerald-400' :
                          c.relevance_score >= 0.6 ? 'border-amber-400/30 text-amber-400' :
                          'border-muted-foreground/30 text-muted-foreground'
                        }`}>
                          {Math.round((c.relevance_score || 0) * 100)}% {lang === 'pl' ? 'trafność' : 'match'}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mb-2">
                        {c.name} {c.region ? `• ${c.region}` : ''} {c.industry ? `• ${c.industry}` : ''}
                      </p>
                      
                      {c.specialization && (
                        <p className="text-sm text-muted-foreground mb-2">{c.specialization}</p>
                      )}

                      <div className="grid grid-cols-2 gap-2 mb-2">
                        {c.estimated_capacity && (
                          <div className="text-xs">
                            <span className="text-muted-foreground">{lang === 'pl' ? 'Moce' : 'Capacity'}:</span>
                            <span className="ml-1">{c.estimated_capacity}</span>
                          </div>
                        )}
                        {c.price_range && (
                          <div className="text-xs">
                            <span className="text-muted-foreground">{lang === 'pl' ? 'Cena' : 'Price'}:</span>
                            <span className="ml-1">{c.price_range}</span>
                          </div>
                        )}
                      </div>

                      {/* Strengths & Risks */}
                      <div className="flex flex-wrap gap-1 mb-2">
                        {(c.strengths || []).map((s, si) => (
                          <Badge key={si} variant="secondary" className="text-xs py-0 gap-1">
                            <Star className="w-2.5 h-2.5 text-emerald-400" /> {s}
                          </Badge>
                        ))}
                        {(c.risks || []).map((r, ri) => (
                          <Badge key={ri} variant="outline" className="text-xs py-0 gap-1 border-amber-400/30 text-amber-400">
                            <AlertTriangle className="w-2.5 h-2.5" /> {r}
                          </Badge>
                        ))}
                      </div>

                      {/* Certifications */}
                      {c.certifications && c.certifications.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {c.certifications.map((cert, ci) => (
                            <Badge key={ci} variant="outline" className="text-xs py-0 gap-1 border-primary/30 text-primary">
                              <Shield className="w-2.5 h-2.5" /> {cert}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Add button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleAddContractor(c)}
                      disabled={addingId === c.company}
                      className="flex-shrink-0 ml-3 gap-1"
                      data-testid={`add-research-contractor-${i}`}
                    >
                      {addingId === c.company ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <UserPlus className="w-3.5 h-3.5" />
                      )}
                      {lang === 'pl' ? 'Dodaj' : 'Add'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!loading && !results && (
        <Card className="border border-border">
          <CardContent className="text-center py-16">
            <Search className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="text-sm font-medium mb-1">
              {lang === 'pl' ? 'Wyszukaj kontrahentów' : 'Search for contractors'}
            </h3>
            <p className="text-xs text-muted-foreground max-w-md mx-auto">
              {lang === 'pl'
                ? 'Opisz czego szukasz - Adam przeszuka rynek i znajdzie najlepszych potencjalnych kontrahentów z analizą ryzyk i rekomendacjami.'
                : 'Describe what you need - Adam will search the market and find the best potential contractors with risk analysis and recommendations.'}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
